import {
  ApplicationRef,
  ChangeDetectorRef,
  ComponentFactoryResolver,
  Directive,
  ElementRef,
  Injector, Input, Renderer2,
} from '@angular/core';
import {EventsHttpService} from '@api/http/events/events-http.service';
import {
  AppPromotionEmbedConfig, AppStoreButtonsEmbedConfig,
  DefaultEmbedConfig,
  EmbedType, FollowBannerEmbedConfig, GoogleAdEmbedConfig,
  LinkButtonEmbedConfig,
  MatchTeaserEmbedConfig, RelatedNewsEmbedConfig,
  VotingEmbedConfig
} from '@shared/modules/shared-news/directives/embeds.config';
import {INewsPost} from '@api/models/news/news';

@Directive({
  selector: '[appDynamicComponentEmbed]'
})
export class DynamicComponentEmbedDirective {
  static readonly TAG_NAME = 'fanat-widget';

  // tags will only be replaced and filled if this is true
  @Input() dynamicEmbedsActive: boolean = true;

  /**
   * During sanitation in reporter, HTML tag symbols get replaced by &lt; and &gt;
   * This function replaces the sanitized tags in the string that is supposed to be injected into the innerHtml of the target element
   * it also closes tags so that injection does not break display of surrounding elements
   * @param innerHtml to replace tags in
   */
  static replaceSanitizedTags(innerHtml: string): string {
    const tagDetectionRegex = new RegExp(`(&lt;|<)(${DynamicComponentEmbedDirective.TAG_NAME}(?!(&gt;|>))[^&>]*)(&gt;|>)`, 'gm');
    let match = tagDetectionRegex.exec(innerHtml);
    let nrWidgets = 0;

    while (match != null) {
      nrWidgets++;
      const indexString = `data-index="${nrWidgets}"`; // add index, else replace() below will not work with tags that are not unique
      const matchedTag = match[0];
      const tagContent = match[2];
      innerHtml = innerHtml.replace(matchedTag, `<${tagContent + indexString}></${DynamicComponentEmbedDirective.TAG_NAME}>`);
      match = tagDetectionRegex.exec(innerHtml);
    }

    return innerHtml;
  }

  constructor(private ref: ComponentFactoryResolver,
              private changeDetectorRef: ChangeDetectorRef,
              private elementRef: ElementRef,
              private appRef: ApplicationRef,
              private injector: Injector,
              private eventService: EventsHttpService,
              private renderer: Renderer2,
              private resolver: ComponentFactoryResolver) { }

  public async renderEmbeddings(parentNewsPost: INewsPost) {
    if (!this.dynamicEmbedsActive) {
      return;
    }

    const embeddings = this.getEmbeddingElements();

    for (const embeddingElement of embeddings) {
      const config = await this.readEmbeddingConfig(embeddingElement);
      if (!config) {
        console.log('Error reading embed config', embeddingElement);
        continue;
      }

      config.placeholderElement = embeddingElement;
      config.parentNews = parentNewsPost;
      await this.injectComponent(config);
    }
  }

  private async injectComponent(config: DefaultEmbedConfig<unknown>): Promise<void> {
    const factory = this.resolver.resolveComponentFactory(config.associatedComponentType);
    const compRef = factory.create(this.injector, [], config.placeholderElement);
    await config.initComponent(compRef);

    this.appRef.attachView(compRef.hostView);

    this.changeDetectorRef.detectChanges();
  }

  private readEmbeddingConfig(element: HTMLElement): DefaultEmbedConfig<unknown> {
    const type = element.getAttribute('type');
    let config = null;

    switch (type) {
      case EmbedType.Voting:
        config = new VotingEmbedConfig();
        config.id = element.getAttribute('id');
        break;
      case EmbedType.MatchTeaser:
        config = new MatchTeaserEmbedConfig();
        config.eventService = this.eventService;
        config.id = element.getAttribute('id');
        break;
      case EmbedType.AppPromotion:
        config = new AppPromotionEmbedConfig();
        break;
      case EmbedType.GoogleAd:
        config = new GoogleAdEmbedConfig();
        break;
      case EmbedType.FollowBanner:
        config = new FollowBannerEmbedConfig();
        config.entityType = element.getAttribute('entity');
        config.entityReadableId = element.getAttribute('target');
        break;
      case EmbedType.AppStoreButtons:
        config = new AppStoreButtonsEmbedConfig();
        break;
      case EmbedType.RelatedNews:
        config = new RelatedNewsEmbedConfig();
        config.count = element.getAttribute('count');
        config.relatedArticleIds = element.getAttribute('ids');
        break;
      case EmbedType.LinkButton:
        config = new LinkButtonEmbedConfig();
        config.text = element.getAttribute('text');
        config.url = element.getAttribute('url');
        config.color = element.getAttribute('color');
        config.background = element.getAttribute('background');
        break;
      default:
        break;
    }

    return config;
  }

  private getEmbeddingElements(type?: EmbedType): HTMLElement[] {
    const elements = Array.from<HTMLElement>(this.elementRef.nativeElement.getElementsByTagName(DynamicComponentEmbedDirective.TAG_NAME));
    return elements.filter(element => !type ||  element.getAttribute('type') === type);
  }

  /**
   * check if host element contains placeholder for voting widget
   * if not, inject placeholder at end
   * if present placeholder has no id (default if you just want to display voting associated with newspost), set id attribute
   * @param votingId to inject
   */
  public prepareVotingInjection(votingId: string): void {
    if (!this.dynamicEmbedsActive) {
      return;
    }

    const votingEmbeddings = this.getEmbeddingElements(EmbedType.Voting);

    if (!votingEmbeddings.length) {
      const votingElement = this.renderer.createElement(DynamicComponentEmbedDirective.TAG_NAME);
      votingElement.setAttribute('type', EmbedType.Voting);
      this.renderer.appendChild(this.elementRef.nativeElement, votingElement);
      votingEmbeddings.push(votingElement);
    }

    for (const votingEmbedding of votingEmbeddings) {
      if (!votingEmbedding.getAttribute('id')) {
        this.renderer.setAttribute(votingEmbedding, 'id', votingId);
      }
    }
  }
}
